import React from 'react';
import { cn } from '@/lib/utils';
import { CheckCircle2, XCircle, AlertCircle, Info, LucideIcon } from 'lucide-react';

interface AlertConfig {
  bg: string;
  border: string;
  icon: LucideIcon;
  iconColor: string;
  titleColor: string;
  textColor: string;
}

const alertConfig: Record<string, AlertConfig> = {
  success: {
    bg: 'bg-emerald-50 dark:bg-emerald-950',
    border: 'border-emerald-200 dark:border-emerald-800',
    icon: CheckCircle2,
    iconColor: 'text-emerald-500 dark:text-emerald-400',
    titleColor: 'text-emerald-800 dark:text-emerald-200',
    textColor: 'text-emerald-700 dark:text-emerald-300',
  },
  error: {
    bg: 'bg-rose-50 dark:bg-rose-950',
    border: 'border-rose-200 dark:border-rose-800',
    icon: XCircle,
    iconColor: 'text-rose-500 dark:text-rose-400',
    titleColor: 'text-rose-800 dark:text-rose-200',
    textColor: 'text-rose-700 dark:text-rose-300',
  },
  warning: {
    bg: 'bg-amber-50 dark:bg-amber-950',
    border: 'border-amber-200 dark:border-amber-800',
    icon: AlertCircle,
    iconColor: 'text-amber-500 dark:text-amber-400',
    titleColor: 'text-amber-800 dark:text-amber-200',
    textColor: 'text-amber-700 dark:text-amber-300',
  },
  info: {
    bg: 'bg-blue-50 dark:bg-blue-950',
    border: 'border-blue-200 dark:border-blue-800',
    icon: Info,
    iconColor: 'text-blue-500 dark:text-blue-400',
    titleColor: 'text-blue-800 dark:text-blue-200',
    textColor: 'text-blue-700 dark:text-blue-300',
  },
};

interface StatusAlertProps {
  variant?: 'success' | 'error' | 'warning' | 'info';
  title?: string;
  message?: string;
  action?: React.ReactNode;
  className?: string;
}

export const StatusAlert: React.FC<StatusAlertProps> = ({
  variant = 'info',
  title,
  message,
  action,
  className,
}) => {
  const config = alertConfig[variant];
  const Icon = config.icon;

  return (
    <div
      data-testid={`status-alert-${variant}`}
      className={cn(
        'p-4 rounded-lg border',
        config.bg,
        config.border,
        className
      )}
    >
      <div className="flex gap-3">
        <Icon className={cn('w-5 h-5 flex-shrink-0 mt-0.5', config.iconColor)} />
        <div className="flex-1">
          {title && (
            <h4
              data-testid="alert-title"
              className={cn('font-semibold text-sm mb-1', config.titleColor)}
            >
              {title}
            </h4>
          )}
          {message && (
            <p
              data-testid="alert-message"
              className={cn('text-sm', config.textColor)}
            >
              {message}
            </p>
          )}
          {action && (
            <div className="mt-3">
              {action}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
